import { Link } from 'react-router-dom';
import { Phone, ChevronRight, Clock, MapPin, Truck, Wrench, Package, Repeat, Star } from 'lucide-react';
import { SEOHead, getLocalBusinessSchema, SEO_BASE } from './SEO';

const TESTIMONIALS = [
  { name: "Sarah J.", city: "Orangevale", text: "Awesome service my second time using this company. Fast, reliable, and professional!" },
  { name: "Michael C.", city: "Citrus Heights", text: "A2B towing arrived quickly (25 minutes). Excellent professional service. Outstanding customer service. Would recommend them to my family and friends." },
  { name: "Emily R.", city: "Folsom", text: "ETA said 60-90 minutes but they arrived in 15. The driver was so kind and even tested the battery. So kind, helpful and on point." },
  { name: "David T.", city: "Roseville", text: "I've used this company twice now. They've been great, very responsive and quick to get our work van towed. I highly recommend them!" },
  { name: "Lisa M.", city: "Orangevale", text: "Very pleasant tow truck driver, Vince. Excellent hooking up and unloading of my car. It was overall a wonderful experience!" },
  { name: "James W.", city: "Rancho Cordova", text: "Very fast and friendly service. Car transmission broke on the way to Tahoe. A2B was there quickly and got me back to Sacramento." },
  { name: "Jennifer B.", city: "Granite Bay", text: "Had a flat tire on Highway 50 late at night. A2B came out within 40 minutes and changed my tire safely. Driver was courteous and professional. Highly recommend!" },
  { name: "Robert G.", city: "Folsom", text: "My classic car needed flatbed towing to avoid any damage. A2B handled it with extreme care and professionalism. The driver knew exactly what he was doing. Will use again!" },
  { name: "Amanda F.", city: "Citrus Heights", text: "Locked my keys in the car at the grocery store. Called A2B and they had someone there in 35 minutes. Quick, affordable, and super friendly service!" },
];

const SERVICES = [
  { icon: Truck, title: "Towing Services", desc: "Fast and reliable towing available anytime. Our tow trucks are ready to help with any vehicle size, from cars to larger personal trucks." },
  { icon: Wrench, title: "Roadside Assistance", desc: "Stuck on the road? We offer quick roadside help including jump starts, tire changes, fuel delivery, and lockout services." },
  { icon: Package, title: "Hauling Services", desc: "Professional hauling services for cars, trucks, and equipment. Our skilled team uses the right tools and methods to move your vehicles safely." },
  { icon: Repeat, title: "Load Transfer", desc: "Need to transfer your load from one vehicle to another? Our experienced team handles load transfers with care and efficiency." },
];

function HeroSection() {
  return (
    <section className="relative h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 bg-black">
        <img alt="A2B Towing emergency roadside assistance service truck helping stranded vehicle in Orangevale, Sacramento County" className="w-full h-full object-cover opacity-40" src="https://images.unsplash.com/photo-1529369623266-f5264b696110?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxyb2Fkc2lkZSUyMGFzc2lzdGFuY2V8ZW58MHx8fHwxNzYyMjgxMDM2fDA&ixlib=rb-4.1.0&q=85" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
      </div>
      <div className="relative container mx-auto px-4 z-10">
        <div className="max-w-3xl">
          <div className="inline-block mb-4 px-4 py-2 bg-red-600/20 border border-red-600 rounded-full backdrop-blur-sm">
            <span className="text-red-600 text-sm font-semibold">Emergency Service Available 24/7</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight" data-testid="hero-heading">
            Fast & Reliable<span className="text-red-600"> Towing Service</span>
          </h1>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">A2B Towing & Roadside Assistance is Orangevale's most trusted towing company. Available anytime for all your emergency towing and roadside assistance needs.</p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/quote" data-testid="hero-quote-btn">
              <button className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 text-lg font-semibold rounded-md transition-all hover:scale-105 inline-flex items-center gap-2">Automatic Quote Generator <ChevronRight className="w-5 h-5" /></button>
            </Link>
            <a href="tel:+19162343923" data-testid="hero-phone-btn">
              <button className="border border-white text-white hover:bg-white hover:text-black px-8 py-4 text-lg font-semibold rounded-md transition-all inline-flex items-center gap-2"><Phone className="w-5 h-5" /> (916) 234-3923</button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function InfoBar() {
  return (
    <section className="bg-red-600 py-6" data-testid="info-bar">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
          <div className="flex items-center justify-center space-x-3 text-white"><Clock className="w-6 h-6" aria-hidden="true" /><span className="font-semibold">Emergency Service Available 24/7</span></div>
          <div className="flex items-center justify-center space-x-3 text-white"><MapPin className="w-6 h-6" aria-hidden="true" /><span className="font-semibold">Serving Orangevale & Surrounding Areas</span></div>
          <div className="flex items-center justify-center space-x-3 text-white"><Phone className="w-6 h-6" aria-hidden="true" /><span className="font-semibold">Fast Response Times</span></div>
        </div>
      </div>
    </section>
  );
}

function AboutPreview() {
  return (
    <section className="py-20 bg-zinc-950" data-testid="about-section">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-block mb-4 px-4 py-2 bg-red-600/10 border border-red-600/30 rounded-full"><span className="text-red-600 text-sm font-semibold">About Us</span></div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Orangevale's Most Trusted Towing Company</h2>
            <p className="text-gray-400 text-lg mb-6 leading-relaxed">A2B Towing & Roadside Assistance started with a simple goal - to be the most trusted towing company in Orangevale, CA. Our team brings years of experience in emergency towing and roadside help.</p>
            <p className="text-gray-400 text-lg mb-8 leading-relaxed">We focus on quick response times and putting our customers first. Our tow truck operators are trained professionals who handle every job with care and skill.</p>
            <Link to="/about" data-testid="about-learn-more">
              <button className="border border-red-600 text-red-600 hover:bg-red-600 hover:text-white px-6 py-3 rounded-md text-sm font-semibold transition-all inline-flex items-center gap-2">Learn More About Us <ChevronRight className="w-4 h-4" /></button>
            </Link>
          </div>
          <div className="relative">
            <img alt="Professional tow truck operator providing fast reliable towing service in Sacramento County" className="rounded-lg shadow-2xl w-full h-[500px] object-cover" src="https://images.pexels.com/photos/9518248/pexels-photo-9518248.jpeg" loading="lazy" />
            <div className="absolute -bottom-6 -left-6 bg-red-600 text-white p-8 rounded-lg shadow-xl">
              <div className="text-5xl font-bold mb-2">10+</div>
              <div className="text-sm">Years of Experience</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ServicesGrid() {
  return (
    <section className="py-20 bg-black" data-testid="services-section">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="inline-block mb-4 px-4 py-2 bg-red-600/10 border border-red-600/30 rounded-full"><span className="text-red-600 text-sm font-semibold">Our Services</span></div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Complete Towing & Roadside Solutions</h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">From emergency towing to roadside assistance, we've got you covered anytime.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {SERVICES.map(svc => (
            <div key={svc.title} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 hover:border-red-600 transition-all group" data-testid={`service-card-${svc.title.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="w-12 h-12 bg-red-600/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-red-600 transition-colors">
                <svc.icon className="w-6 h-6 text-red-600 group-hover:text-white transition-colors" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{svc.title}</h3>
              <p className="text-gray-400 text-sm mb-4">{svc.desc}</p>
              <Link to="/services" className="text-red-600 text-sm font-semibold hover:underline inline-flex items-center">Learn More <ChevronRight className="ml-1 w-4 h-4" /></Link>
            </div>
          ))}
        </div>
        <div className="text-center mt-12">
          <Link to="/services"><button className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 text-lg font-semibold rounded-md transition-all">View All Services</button></Link>
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  return (
    <section className="py-20 bg-zinc-950" data-testid="testimonials-section">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="inline-block mb-4 px-4 py-2 bg-red-600/10 border border-red-600/30 rounded-full"><span className="text-red-600 text-sm font-semibold">Testimonials</span></div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">What Our Customers Say</h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">Don't just take our word for it - hear from our satisfied customers.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t) => (
            <div key={`${t.name}-${t.city}`} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6" data-testid={`testimonial-${t.name.replace(/\s+/g, '-').toLowerCase()}`}>
              <div className="flex mb-4">{['s1','s2','s3','s4','s5'].map((sid) => <Star key={sid} className="w-4 h-4 fill-red-600 text-red-600" />)}</div>
              <p className="text-gray-300 mb-4 italic">"{t.text}"</p>
              <p className="text-white font-semibold">{t.name} <span className="text-gray-500 font-normal text-sm">- {t.city}, CA</span></p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function GoogleReviewCTA() {
  return (
    <section className="py-12 bg-zinc-900" data-testid="google-review-section">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-1 mb-3">
            {['gs1','gs2','gs3','gs4','gs5'].map((sid) => (<Star key={sid} className="w-6 h-6 fill-yellow-500 text-yellow-500" />))}
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">Rated 5.0 Stars on Google</h2>
          <p className="text-gray-400 mb-5">Join our satisfied customers. Your feedback helps us serve the Sacramento community better.</p>
          <a href="https://www.google.com/maps/place/A2B+Towing+%26+Roadside+Assistance" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white text-black hover:bg-gray-100 px-6 py-3 rounded-md font-semibold transition-all" data-testid="google-review-btn">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Review Us on Google
          </a>
        </div>
      </div>
    </section>
  );
}

function ServiceAreasGrid() {
  return (
    <section className="py-20 bg-black" data-testid="areas-section">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="inline-block mb-4 px-4 py-2 bg-red-600/10 border border-red-600/30 rounded-full"><span className="text-red-600 text-sm font-semibold">Service Areas</span></div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Serving Orangevale & Surrounding Areas</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
          {SEO_BASE.serviceAreas.map(area => (
            <Link key={area} to={`/service-area/${area.toLowerCase().replace(/\s+/g, '-')}`} className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 text-center hover:border-red-600 transition-all block" data-testid={`area-${area.toLowerCase().replace(/\s+/g, '-')}`}>
              <MapPin className="w-5 h-5 text-red-600 mx-auto mb-2" aria-hidden="true" />
              <span className="text-white font-medium">{area}</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="py-20 bg-red-600" data-testid="cta-section">
      <div className="container mx-auto px-4 text-center">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Need Emergency Towing or Roadside Assistance?</h2>
        <p className="text-white/90 text-xl mb-8 max-w-2xl mx-auto">We're available 24/7 to help you get back on the road. Fast response times guaranteed.</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a href="tel:+19162343923" data-testid="cta-phone">
            <button className="bg-white text-red-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold rounded-md transition-all hover:scale-105 inline-flex items-center gap-2"><Phone className="w-5 h-5" /> Call Now: (916) 234-3923</button>
          </a>
          <Link to="/quote" data-testid="cta-quote">
            <button className="border border-white text-white hover:bg-white hover:text-red-600 px-8 py-4 text-lg font-semibold rounded-md transition-all inline-flex items-center gap-2">Request a Quote <ChevronRight className="w-5 h-5" /></button>
          </Link>
        </div>
      </div>
    </section>
  );
}

function HomePage() {
  return (
    <>
      <SEOHead
        title="A2B Towing & Roadside Assistance - Orangevale CA"
        description="Professional towing & roadside assistance in Orangevale, CA. Fast response, flatbed towing, jump starts, tire changes. Call (916) 234-3923."
        canonical="/"
        schema={getLocalBusinessSchema()}
      />
      <HeroSection />
      <InfoBar />
      <AboutPreview />
      <ServicesGrid />
      <TestimonialsSection />
      <GoogleReviewCTA />
      <ServiceAreasGrid />
      <CTASection />
    </>
  );
}

export default HomePage;
export { TESTIMONIALS, SERVICES };
